// Step 3 — Review & Generate. (Was Step 4 before Settings was dropped.)
//
// Summarizes the selections from steps 1–2 so the operator can sanity-
// check before kicking off the render. POST /api/ads/generate is
// async — the server creates a CampaignRun, returns 202 immediately,
// and renders in the background. We route to /ads?campaignRunId=X
// where the page polls /api/ads/runs/:id to show progress as
// creatives stream in.
//
// Templates / CTA copy / landing URL are no longer authored in the
// wizard. The wizard plugs in defaults (all 5 ai_* templates, empty
// CTA strings, empty UTMs); the backend fills in CTA text from
// copy_candidates at render time and falls back to brand.websiteUrl
// for ctaUrl. The summary below shows only the operator-authored
// picks (campaign + products + media).

import { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Text, Button, Divider, Badge, Tooltip, useToast, Spinner, Image,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { apiJson, isApiError } from '../../auth/apiFetch';
import { useAdReadiness } from '../../brand/useAdReadiness';
import { useBrand } from '../../brand/BrandContext';
import type { WizardSelections, SelectionSummary } from './index';
import { StepShell } from './index';
import { WizardBriefEditor } from './WizardBriefEditor';
import type { WorkflowEntry } from './formatCatalog';
import { sceneReelGate, sceneReelGateMessage, sceneReelRefusalMessage, sceneReelRequestBody } from './sceneReelRequest';
import { estimateExceedsCap } from '../../spend/cap';

type Props = {
  value: WizardSelections;
  // Non-null only in scene-reel mode (the wizard passes the live workflow
  // entry when value.workflow === 'scene_reel'). Null keeps every standard
  // path below exactly as it was.
  sceneReelWorkflow?: WorkflowEntry | null;
};

type GenerateResponse = {
  campaignRunId: string;
  campaignId:    string;
  brandId:       string;
  campaignKind:  string | null;
  total:         number;
  status:        'running' | 'done' | 'failed';
  // Non-blocking: a DIFFERENT request is already running over some of the same
  // products. Allowed on purpose — the operator is told, not stopped.
  notice?: {
    code:       string;
    message:    string;
    runId:      string | null;
    productIds: string[];
  } | null;
};

// The 409 body the backend sends when this exact request is already running, or
// already ran. `confirmable` means the operator may run it anyway; to do so the
// same body is re-POSTed with confirmDuplicate:true and acknowledgeRunId echoed
// back as acknowledgedRunId. That echo is what makes the override single-use —
// a stray second confirm is refused against the run the first one just started.
type DuplicateConflict = {
  error:            string;
  code:             string;
  reason:           'duplicate-in-flight' | 'duplicate-of-previous' | 'raced-concurrent-run';
  confirmable:      boolean;
  acknowledgeRunId: string | null;
  runId:            string | null;
  startedAt:        string | null;
};

type PreviewResponse = {
  total:         number;
  byProduct:     Record<string, number>;
  byVariantKind: { ugc: number; product_image: number };
  seedCount:     number;
  productCount:  number;
  // Present after Phase 3 dry-run: split of video ads by expansion mode.
  byMode?:       { deterministic: number; director: number };
  // NEW (backend feat/express-count): what the run actually CHARGES for, as
  // opposed to `total`, which is what it DELIVERS. A PMax video run mints far
  // more creatives than it bills — per product: 2 billable Omni masters
  // (9:16 + 16:9) + 1 free 1:1 crop + 3 free funnel re-titles per surface — so
  // `total` alone reads as "48 creatives" when only 8 of those are Omni/Atlas
  // submits. Optional because an older backend that hasn't shipped this block
  // yet must not break the UI — every reader MUST treat a missing `billable`
  // as "say nothing", never as "assume zero" or back-derive a guess from
  // `total`/`byMode` (those count ads, not submits, and would misreport).
  billable?: {
    videoMasters: number; // Omni submits — cost money
    images:       number; // Atlas image submits — cost money
    freeDerived:  number; // crops + funnel re-titles — free
    // Scene reel only (API contract §3). A scene reel bills scene clips and
    // stills, never videoMasters/images, so a reader that only looked at those
    // two would call a paid reel free (formatBillableSummary's scene arm).
    sceneClips?:  number;
    stills?:      { min: number; max: number };
  };
  // Scene reel only (API contract §3). Absent on every standard preview.
  sceneReel?: SceneReelPreview;
};

export type SceneReelPreview = {
  model:           string;
  outputs:         string[];
  cut:             number[];
  clipsPerProduct: number;
  estUsd?: {
    perProduct: { low: number; high: number };
    run:        { low: number; high: number };
    ceiling:    number;
  };
  assets?: Array<{
    productId:      string;
    usable:         boolean;
    lifestyle?:     number;
    onModel?:       number;
    productOnly?:   number;
    closingSource?: string | null;
    personasNeeded?: number;
    // Sent by the backend beyond contract §3 (summarizeForPreview): images
    // still unlabelled (labelled at Generate) and why a product is unusable
    // ('scene-reel-assets-unusable' | 'product-not-found'). Display reads the
    // human `warnings` the backend writes from the same facts.
    pendingLabels?: number;
    reason?:        string | null;
    warnings?:      string[];
  }>;
};

// Cap the review strip so a 50-product selection can't blow the layout.
const SUMMARY_THUMB_CAP = 8;
const THUMB_PX = 44;

function planBinding(value: WizardSelections): Record<string, string | number> {
  if (!value.planId || !value.planLineKey || !value.planRevision) return {};
  const planRevision = /^\d+$/.test(value.planRevision) ? Number(value.planRevision) : value.planRevision;
  const binding: Record<string, string | number> = {
    planId: value.planId,
    planLineKey: value.planLineKey,
    planRevision
  };
  if (value.creativeType) binding.creativeType = value.creativeType;
  if (typeof value.capUsd === 'number' && Number.isFinite(value.capUsd) && value.capUsd >= 0) binding.capUsd = value.capUsd;
  if (typeof value.generateCount === 'number' && Number.isInteger(value.generateCount) && value.generateCount > 0) {
    binding.generateCount = value.generateCount;
  }
  return binding;
}

// A dollar figure the dry run actually returned. Creative counts are not
// dollars, and this does not price a billable count — per-unit prices are
// not pinned in the UI.
function dollarEstimate(preview: PreviewResponse | null): number | null {
  if (!preview) return null;
  const record = preview as PreviewResponse & Record<string, unknown>;
  for (const key of ['estimatedCostUsd', 'estimatedUsd', 'estimateUsd'] as const) {
    const amount = record[key];
    if (typeof amount === 'number' && Number.isFinite(amount) && amount >= 0) return amount;
  }
  return null;
}

export function Step4Generate({ value, sceneReelWorkflow = null }: Props) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const capUsd = typeof value.capUsd === 'number' && Number.isFinite(value.capUsd) ? value.capUsd : null;
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSettled, setPreviewSettled] = useState(false);
  // previewSettled stays false until the dry run for the current selections
  // has landed or been skipped, so a capped run cannot click through on the
  // first frame before the estimate exists.
  // Scene-reel mode only: why the free dry run refused (e.g. 503 no renderer,
  // 400 SCENE_REEL_MODEL_UNAVAILABLE), shown instead of a silent blank so the
  // operator learns it before clicking Generate. Standard mode never sets it.
  const [scenePreviewError, setScenePreviewError] = useState<string | null>(null);
  // Set when the backend refused this request as a duplicate AND said the
  // operator may override. Null the rest of the time.
  const [conflict, setConflict] = useState<DuplicateConflict | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const readiness = useAdReadiness();
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.id || null;
  const gateDisabled = !readiness.ready;
  const gateTip = gateDisabled
    ? [readiness.reason, ...(readiness.blockers || []).map(b => `• ${b.message}`)].filter(Boolean).join('\n')
    : '';

  // Lifted from Step 2 when the operator walked the normal path. Deep-
  // link into ?step=generate leaves this empty — the effect below
  // hydrates campaign name + media thumbs; products degrade to count
  // (no products-by-ids endpoint).
  const lifted = value.selectionSummary || { products: [], media: [] };
  const [fallbackSummary, setFallbackSummary] = useState<SelectionSummary | null>(null);
  const summary: SelectionSummary = {
    campaignName: lifted.campaignName || fallbackSummary?.campaignName,
    products:     lifted.products.length ? lifted.products : (fallbackSummary?.products || []),
    media:        lifted.media.length    ? lifted.media    : (fallbackSummary?.media    || [])
  };

  useEffect(() => {
    // Need something Step 2 didn't already give us.
    const needCampaign = !!value.campaignId && !lifted.campaignName;
    const needMedia    = value.mediaIds.length > 0 && lifted.media.length === 0 && !!brandId;
    if (!needCampaign && !needMedia) {
      setFallbackSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      let campaignName: string | undefined;
      let media: SelectionSummary['media'] = [];
      try {
        if (needCampaign && value.campaignId) {
          const r = await apiJson<{ campaign?: { name?: string } }>(
            `/api/campaigns/${encodeURIComponent(value.campaignId)}`
          );
          campaignName = r.campaign?.name || undefined;
        }
      } catch { /* best-effort */ }
      try {
        if (needMedia && brandId) {
          const r = await apiJson<{ media: Array<{ id?: string; mediaId?: string; fileType?: string; fileUrl?: string }> }>(
            `/api/media?brandId=${encodeURIComponent(brandId)}&ids=${value.mediaIds.join(',')}`
          );
          media = (r.media || []).map(m => {
            const id = m.mediaId || m.id || '';
            const fileUrl = m.fileUrl || '';
            let thumbUrl = fileUrl;
            if (m.fileType === 'video' && fileUrl.includes('/video/upload/')) {
              thumbUrl = fileUrl
                .replace('/video/upload/', '/video/upload/so_0/')
                .replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, '.jpg$2');
            }
            return { id, thumbUrl };
          }).filter(m => m.id && m.thumbUrl);
        }
      } catch { /* best-effort */ }
      if (!cancelled) {
        setFallbackSummary({ campaignName, products: [], media });
      }
    })();
    return () => { cancelled = true; };
    // lifted.campaignName / lifted.media.length intentionally in deps so
    // a late Step 2 patch cancels the fallback path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.campaignId, value.mediaIds.join(','), brandId, lifted.campaignName, lifted.media.length]);

  // Naive client-side count is kept as the loading placeholder while
  // the backend dry-run resolves. The backend dry-run is authoritative
  // — it accounts for matchedMedia fan-in, alt expansion, per-product
  // cap (3), and the global MAX_ADS cap, none of which the client can
  // see without re-implementing the seed engine.
  const seedCount      = value.productIds.length + value.mediaIds.length;
  // Nothing ticked in the format picker → the backend would refuse with
  // NO_GENERATABLE_FORMAT rather than mint a run that expands to zero ads.
  const noFormatSelected =
    value.staticFormatKeys.length === 0 && value.videoFormatKeys.length === 0;
  const naiveEstimate  = seedCount * value.templateIds.length;
  const totalCreatives = preview?.total ?? naiveEstimate;
  // Scene reel replaces the format gate (a scene reel ignores the ticked
  // surfaces) with its own: this brand on the allowlist (readiness
  // `sceneReel`, contract §2a) and 1..maxProducts products. Null in standard
  // mode.
  const sceneReelMode = value.workflow === 'scene_reel';
  const sceneGate = sceneReelMode
    ? sceneReelGate(value.productIds.length, sceneReelWorkflow, readiness.sceneReel)
    : null;
  const sceneSeedCap = sceneReelWorkflow?.maxSeedPicksPerProduct ?? 3;

  // Fetch the dry-run preview on entry + whenever selections change.
  // Skips when the form isn't valid enough to run expansion (no
  // campaign, no templates, no seeds).
  useEffect(() => {
    if (!value.campaignId || !value.templateIds.length || (sceneReelMode ? !!sceneGate : seedCount === 0)) {
      setPreview(null);
      setScenePreviewError(null);
      setPreviewLoading(false);
      setPreviewSettled(true);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewSettled(false);
    setScenePreviewError(null);
    const excludePairings = (value.excludedPairings || []).map(s => {
      const [pid, mid] = s.split('|');
      return { productId: pid === 'null' ? null : pid, mediaId: mid };
    });
    const includeVideo = value.adKinds === 'video' || value.adKinds === 'both';
    apiJson<PreviewResponse>('/api/ads/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Scene reel: the shared builder (sceneReelRequest.ts) — the same body
      // buildBody posts. Standard: the literal below, untouched.
      body: JSON.stringify(sceneReelMode ? sceneReelRequestBody(value, sceneSeedCap) : {
        campaignId:  value.campaignId,
        productIds:  value.productIds,
        mediaIds:    value.mediaIds,
        templateIds: value.templateIds,
        cta:         { text: value.ctaText, url: value.ctaUrl },
        urlParams:   value.urlParams,
        platformFormat: value.platformFormat,
        kinds:          value.adKinds,
        // Multi-select surfaces. preset 'explicit' tells the backend to generate
        // exactly these and to ignore platformFormat/kinds/expandStaticFormats
        // for format resolution — each staticFormats entry is its own billable
        // image generation, so this drives the preview's quoted count.
        preset:        'explicit',
        staticFormats: value.staticFormatKeys,
        videoFormats:  value.videoFormatKeys,
        expandStaticFormats: false,
        excludePairings,
        includeCategoryMatched: value.includeCategoryMatched,
        includeBrandMatched:    value.includeBrandMatched,
        ...(includeVideo ? {
          videoDurationSec: value.videoDurationSec,
          directorVariants: value.directorVariants,
          seedPicks:        value.seedPicks || [],
          // Derived, for consumers minted before seedPicks existed. Unique
          // mediaIds in first-pick order; the backend prefers seedPicks.
          seedMediaIds:     [...new Set((value.seedPicks || []).map(p => p.mediaId))],
          ...(value.videoPromptGuidance.trim()
            ? { videoPromptGuidance: value.videoPromptGuidance.trim() }
            : {}),
          ...(value.videoPromptRaw.trim()
            ? { videoPromptRaw: value.videoPromptRaw.trim() }
            : {})
        } : {})
      })
    })
      .then(res => { if (!cancelled) setPreview(res); })
      .catch(err => {
        if (cancelled) return;
        setPreview(null);
        if (sceneReelMode) {
          setScenePreviewError(sceneReelRefusalMessage(err) ?? (err instanceof Error ? err.message : 'Preview unavailable'));
        }
      })
      .finally(()=> { if (!cancelled) { setPreviewLoading(false); setPreviewSettled(true); } });
    return () => { cancelled = true; };
  }, [
    value.campaignId,
    value.productIds.join(','),
    value.mediaIds.join(','),
    value.templateIds.join(','),
    value.ctaText,
    value.ctaUrl,
    value.urlParams,
    value.platformFormat,
    value.adKinds,
    // Load-bearing: the preview's quoted ad count scales with the number of
    // ticked static sizes. Omitted from these deps, ticking a second size would
    // leave the operator looking at the one-size quote while committing to two
    // (or three) times the billable generations.
    value.staticFormatKeys.join(','),
    value.videoFormatKeys.join(','),
    value.videoDurationSec,
    value.directorVariants,
    (value.seedPicks || []).map(p => `${p.productId}:${p.mediaId}`).join(','),
    value.videoPromptGuidance,
    value.videoPromptRaw,
    value.includeCategoryMatched,
    value.includeBrandMatched,
    (value.excludedPairings || []).join(','),
    seedCount,
    // Scene reel inputs. Inert in standard mode (workflow stays 'standard').
    value.workflow,
    value.sceneModel,
    (value.sceneSeedPicks || []).map(p => `${p.productId}:${p.mediaId}`).join(','),
    sceneGate,
    sceneSeedCap
  ]);

  // Built once and reused verbatim by the confirm path. The retry MUST send a
  // byte-identical request apart from the two override fields — the backend
  // fingerprints the request to decide whether it is the same one, so a retry
  // that rebuilt the body differently would hash differently, sail past the
  // duplicate check, and bill a second time without the operator confirming
  // anything.
  const buildBody = (confirm?: { acknowledgedRunId: string }) => {
    // Scene reel: the same builder the preview used, plus the same two
    // override fields appended the same way as the standard body below.
    if (sceneReelMode) {
      return {
        ...sceneReelRequestBody(value, sceneSeedCap),
        ...planBinding(value),
        ...(confirm ? {
          confirmDuplicate:  true,
          acknowledgedRunId: confirm.acknowledgedRunId
        } : {})
      };
    }
    // Decode the Step 2 picker's pairing-exclusions back into the
    // shape the backend expects: [{productId|null, mediaId}].
    // Encoded as `${productId||'null'}|${mediaId}` strings.
    const excludePairings = (value.excludedPairings || []).map(s => {
      const [pid, mid] = s.split('|');
      return { productId: pid === 'null' ? null : pid, mediaId: mid };
    });
    const includeVideo = value.adKinds === 'video' || value.adKinds === 'both';
    return {
      campaignId:  value.campaignId,
      productIds:  value.productIds,
      mediaIds:    value.mediaIds,
      templateIds: value.templateIds,
      cta:         { text: value.ctaText, url: value.ctaUrl },
      urlParams:   value.urlParams,
      platformFormat: value.platformFormat,
      kinds:          value.adKinds,
      // Multi-select surfaces — see the preview call above. MONEY: one billable
      // image generation per staticFormats entry, per concept; videoFormats is
      // clamped to one master server-side however many are sent.
      preset:        'explicit',
      staticFormats: value.staticFormatKeys,
      videoFormats:  value.videoFormatKeys,
      expandStaticFormats: false,
      excludePairings,
      includeCategoryMatched: value.includeCategoryMatched,
      includeBrandMatched:    value.includeBrandMatched,
      ...(includeVideo ? {
        videoDurationSec: value.videoDurationSec,
        directorVariants: value.directorVariants,
        seedPicks:        value.seedPicks || [],
        // Derived, for consumers minted before seedPicks existed. Unique
        // mediaIds in first-pick order; the backend prefers seedPicks.
        seedMediaIds:     [...new Set((value.seedPicks || []).map(p => p.mediaId))],
        ...(value.videoPromptGuidance.trim()
          ? { videoPromptGuidance: value.videoPromptGuidance.trim() }
          : {}),
        ...(value.videoPromptRaw.trim()
          ? { videoPromptRaw: value.videoPromptRaw.trim() }
          : {})
      } : {}),
      // refresh:true was a smoke-test workaround for the bug where
      // dedupe-hit ads kept their original campaignRunId and made
      // /ads?campaignRunId=X come back empty. Resolved by #111
      // (campaignRunIds[] $addToSet on every run pick), so we now
      // run with dedupe-by-default — re-running the wizard on the
      // same picks correctly surfaces the existing ads in the new
      // run's gallery.
      ...planBinding(value),
      ...(confirm ? {
        confirmDuplicate:  true,
        acknowledgedRunId: confirm.acknowledgedRunId
      } : {})
    };
  };

  const estimateUsd = sceneReelMode
    ? formatSceneReelPreview(preview, { productCount: value.productIds.length, workflow: sceneReelWorkflow }).estimateHighUsd
    : dollarEstimate(preview);
  const overCap = estimateExceedsCap(estimateUsd, capUsd);
  // Hold the button while a capped run's dry run is still in flight. A
  // creative count is not a dollar estimate, so once the dry run lands
  // with no dollar figure the button is free and the cap stays on screen.
  const capBlocked = capUsd != null && (!previewSettled || previewLoading || overCap);

  const generate = async (confirm?: { acknowledgedRunId: string }) => {
    if (capBlocked) return;
    setBusy(true);
    try {
      const res = await apiJson<GenerateResponse>('/api/ads/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody(confirm))
      });
      setConflict(null);
      if (res.notice?.message) {
        // A different run is already covering some of these products. Allowed —
        // just make sure the operator knows both will be billed.
        //
        // The 202 can only carry the overlap notice today
        // ('concurrent-run-shares-products') — the unclaimed-mint notice is
        // computed after claim and only shows up later, on the /runs/:id
        // poll. Key the title off the code anyway so a future notice
        // landing here isn't mislabeled as "another run in progress".
        const title = res.notice.code === 'concurrent-run-shares-products'
          ? 'Another run is in progress'
          : 'Heads up';
        toast({
          title,
          description: res.notice.message,
          status:      'warning',
          duration:    9000,
          isClosable:  true
        });
      }
      // `res.total` is ALWAYS 0 here — POST /api/ads/generate returns 202
      // before expandWizardJob (Director + claim) has run, specifically so
      // Render's edge doesn't cut the request (routes/ads.js: "total: 0 …
      // expand+claim have not run yet"). This toast used to print
      // `${res.total} creative(s) queued`, i.e. it asserted a real-looking
      // but always-wrong "0 creatives queued" the instant the operator
      // clicked Generate — and since nothing ever follows up with the real
      // count, that false "0" was the only number they saw. Say only what
      // is actually known at this instant (the scope of the request); the
      // real per-run count lives on the Ads page's own progress card, which
      // starts polling as soon as we navigate there.
      const scopeLabel = productCount > 0
        ? `${productCount} product${productCount === 1 ? '' : 's'}`
        : mediaCount > 0
          ? `${mediaCount} media item${mediaCount === 1 ? '' : 's'}`
          : 'this batch';
      toast({
        title:       'Generating ads',
        description: `Starting generation for ${scopeLabel} — watching progress on the Ads page.`,
        status:      'info',
        duration:    4000
      });
      // Carry the run's OWN brandId, not the globally-selected brand. The run
      // is keyed { runId, brandId } server-side, so polling it with whatever
      // brand the switcher happens to be on 404s ("run not found") whenever the
      // campaign belongs to a different brand — which then read as "Failed to
      // load ads" even though the ads were rendering fine.
      // campaignId is required so /ads can scope its GET /api/ads query to
      // this campaign instead of falling back to an unscoped, generatedAt-
      // sorted, capped list that a fresh (or dedupe-reused) batch can fall
      // outside of — see Ads/index.tsx's own "Generate more" handler, which
      // already threads campaignId through this same redirect correctly.
      navigate(
        `/ads?campaignRunId=${encodeURIComponent(res.campaignRunId)}` +
        `&campaignId=${encodeURIComponent(res.campaignId)}` +
        (res.brandId ? `&runBrandId=${encodeURIComponent(res.brandId)}` : '')
      );
    } catch (err) {
      // A refusable duplicate is not a failure — it is a question. Show the
      // confirm dialog instead of a red toast so the operator can choose to run
      // it anyway, which is the whole point of the override.
      const body = isApiError(err) && err.status === 409
        ? (err.body as DuplicateConflict | null)
        : null;
      if (body?.confirmable && body.acknowledgeRunId) {
        setConflict(body);
        return;
      }
      const msg = (sceneReelMode && sceneReelRefusalMessage(err))
        || (err instanceof Error ? err.message : 'unknown error');
      toast({ title: 'Generate failed', description: msg, status: 'error', duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const campaignLabel = summary.campaignName || value.campaignId || '—';
  const productCount  = value.productIds.length;
  const mediaCount    = value.mediaIds.length;
  // null on an older backend (no `billable` block) — falls back to today's
  // Output row exactly, no crash, no "undefined", no invented numbers.
  const billableSummary = preview ? formatBillableSummary(preview) : null;
  const sceneSummary = sceneReelMode
    ? formatSceneReelPreview(preview, { productCount, workflow: sceneReelWorkflow })
    : null;

  return (
    <StepShell heading="Review & generate" helper="Confirm selections, then kick off the render.">
      <VStack align="stretch" spacing={4}>
        {value.campaignId && (
          <WizardBriefEditor campaignId={value.campaignId} />
        )}
        <SummaryRow
          label="Campaign"
          value={campaignLabel}
          mono={!summary.campaignName && !!value.campaignId}
        />
        {capUsd != null && (
          <SummaryRow label="Spend cap" value={`$${capUsd.toFixed(2)}`} testId="generate-spend-cap" />
        )}
        {(value.planId || value.planLineKey) && (
          <SummaryRow label="Kinds" value={value.adKinds} testId="wizard-kinds" />
        )}
        {value.lineCampaignRequired && (
          <Text fontSize="sm" color="brand.ink" data-testid="plan-line-campaign">
            This campaign has to be the one on this plan line. A different campaign is refused.
          </Text>
        )}
        <SummaryThumbRow
          label="Products"
          count={productCount}
          countLabel={
            productCount
              ? `${productCount} product${productCount === 1 ? '' : 's'} selected`
              : '— none —'
          }
          items={summary.products.map(p => ({
            id:    p.id,
            thumb: p.imageUrl,
            title: p.title
          }))}
        />
        {/* A scene reel ignores media picks (its scenes come from the
            products' own images), so it does not list them. */}
        {mediaCount > 0 && !sceneReelMode && (
          <SummaryThumbRow
            label="Media"
            count={mediaCount}
            countLabel={`${mediaCount} media selected`}
            items={summary.media.map(m => ({
              id:    m.id,
              thumb: m.thumbUrl,
              title: null
            }))}
          />
        )}
        <Divider my={2} />

        <HStack justify="space-between" align="center">
          {sceneSummary ? (
            <SceneReelOutput summary={sceneSummary} loading={previewLoading} />
          ) : (
            <Box>
              <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">Output</Text>
              <HStack spacing={2} mt={1} align="flex-start">
                <Text fontSize="lg" fontWeight="800" color="brand.ink">{totalCreatives}</Text>
                <Text fontSize="sm" color="brand.muted">
                  creative{totalCreatives === 1 ? '' : 's'}
                  {preview ? (
                    <> ({formatPreviewBreakdown(preview)})</>
                  ) : (
                    <> (estimate; backend dry-run pending)</>
                  )}
                </Text>
                {previewLoading && <Spinner size="xs" color="brand.muted" />}
              </HStack>
              {/* HOW MANY THINGS COST MONEY — deliberately separate from the
                  muted parenthetical above, which describes what the run
                  DELIVERS (mode/kind split). This describes what it CHARGES
                  for, which is the number that actually matters on a one-click
                  express run: a PMax video batch can preview as "48 creatives"
                  while billing for 8. Absent entirely on an older backend
                  (formatBillableSummary returns null) — no placeholder shown. */}
              {billableSummary && (
                <Badge
                  mt={1.5}
                  colorScheme="orange"
                  variant="subtle"
                  fontSize="11px"
                  fontWeight="800"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                >
                  {billableSummary}
                </Badge>
              )}
            </Box>
          )}
          <Tooltip label={gateTip} isDisabled={!gateDisabled} hasArrow whiteSpace="pre-line">
            <Button
              variant="brand"
              size="md"
              // Wrapped, NOT passed by reference: React hands onClick the mouse
              // event as the first argument, which generate() would read as a
              // `confirm` object and send an undefined acknowledgedRunId —
              // silently turning every normal click into an override attempt.
              onClick={() => generate()}
              isLoading={busy}
              loadingText="Generating…"
              // noFormatSelected mirrors the backend's NO_GENERATABLE_FORMAT
              // refusal. With a multi-select picker the operator can now untick
              // every size, and posting that would 400 — so gate the click here
              // and let the picker's own "pick at least one size" line explain it.
              //
              // Scene reel swaps that format gate for its own product gate
              // (sceneReelGate) — the ticked surfaces do not apply to it.
              isDisabled={gateDisabled || capBlocked || (sceneReelMode ? !!sceneGate : (seedCount === 0 || noFormatSelected))}
            >
              Generate Ads
            </Button>
          </Tooltip>
        </HStack>

        {gateDisabled && !readiness.loading && (
          <VStack align="stretch" spacing={1} bg="orange.50" borderWidth="1px" borderColor="orange.300" borderRadius="md" p={3}>
            <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="0.06em">
              Account setup incomplete
            </Text>
            <Text fontSize="sm" color="brand.ink">{readiness.reason}</Text>
            {readiness.blockers.map(b => (
              <Text key={b.code} fontSize="11px" color="brand.muted">• {b.message}</Text>
            ))}
          </VStack>
        )}
        {!gateDisabled && !sceneReelMode && seedCount === 0 && (
          <Badge alignSelf="flex-start" colorScheme="orange" variant="subtle" fontSize="10px">
            Pick at least one product or one media to enable Generate.
          </Badge>
        )}
        {overCap && estimateUsd != null && capUsd != null && (
          <Badge alignSelf="flex-start" colorScheme="orange" variant="subtle" fontSize="10px" whiteSpace="normal" data-testid="generate-over-cap">
            Estimate ${estimateUsd.toFixed(2)} is above the ${capUsd.toFixed(2)} cap.
          </Badge>
        )}
        {!gateDisabled && sceneGate && (
          <Badge alignSelf="flex-start" colorScheme="orange" variant="subtle" fontSize="10px" whiteSpace="normal">
            {sceneReelGateMessage(sceneGate, productCount, sceneReelWorkflow)}
          </Badge>
        )}
        {!gateDisabled && !sceneGate && scenePreviewError && (
          <Box bg="orange.50" borderWidth="1px" borderColor="orange.300" borderRadius="md" p={3}>
            <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="0.06em">
              Scene reel preview refused
            </Text>
            <Text fontSize="sm" color="brand.ink">{scenePreviewError}</Text>
          </Box>
        )}
      </VStack>

      {/*
        DUPLICATE CONFIRM — the backend refused this request because it is
        identical to one already running, or to one that already ran. Owner
        direction: note it, but let the operator run it anyway. Wording leads
        with the cost, because "run it anyway" means a second billable set.
      */}
      <Modal isOpen={!!conflict} onClose={() => setConflict(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            {conflict?.reason === 'duplicate-of-previous'
              ? 'You already ran this'
              : 'This is already generating'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="brand.ink">{conflict?.error}</Text>
              {conflict?.startedAt && (
                <Text fontSize="11px" color="brand.muted">
                  {conflict.reason === 'duplicate-of-previous' ? 'Ran' : 'Started'}{' '}
                  {new Date(conflict.startedAt).toLocaleString()}
                </Text>
              )}
              <Box bg="orange.50" borderWidth="1px" borderColor="orange.300" borderRadius="md" p={3}>
                <Text fontSize="11px" color="orange.700">
                  Running it again produces new creative from new seeds — it does not
                  duplicate the existing ads, and it is billed as a separate generation.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setConflict(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="brand"
              isLoading={busy}
              onClick={() => {
                const ack = conflict?.acknowledgeRunId;
                if (!ack) return;
                setConflict(null);
                // Replays the same body plus the acknowledgement. If a newer
                // identical run has appeared in the meantime the backend refuses
                // again with a fresh id, and this dialog simply reopens.
                void generate({ acknowledgedRunId: ack });
              }}
            >
              Generate anyway
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </StepShell>
  );
}

function SummaryRow({ label, value, mono, testId }: { label: string; value: string; mono?: boolean; testId?: string }) {
  return (
    <HStack justify="space-between" align="flex-start" spacing={4} data-testid={testId}>
      <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" flexShrink={0} pt={0.5} minW="100px">
        {label}
      </Text>
      <Text
        fontSize="sm"
        color="brand.ink"
        fontWeight="600"
        textAlign="right"
        fontFamily={mono ? 'mono' : undefined}
        wordBreak="break-all"
      >
        {value}
      </Text>
    </HStack>
  );
}

// Products / media review strip — count on the right when no thumbs are
// available (deep-link fallback for products); otherwise a horizontal
// thumb row capped at SUMMARY_THUMB_CAP with a "+N more" badge.
function SummaryThumbRow({
  label, count, countLabel, items
}: {
  label:      string;
  count:      number;
  countLabel: string;
  items:      Array<{ id: string; thumb: string | null; title: string | null }>;
}) {
  const visible = items.slice(0, SUMMARY_THUMB_CAP);
  const overflow = Math.max(0, items.length - visible.length);
  const hasThumbs = visible.some(i => i.thumb);

  return (
    <HStack justify="space-between" align="flex-start" spacing={4}>
      <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" flexShrink={0} pt={0.5} minW="100px">
        {label}
      </Text>
      <VStack align="flex-end" spacing={1.5} flex={1} minW={0}>
        <Text fontSize="sm" color="brand.ink" fontWeight="600" textAlign="right">
          {countLabel}
        </Text>
        {hasThumbs && (
          <HStack spacing={2} flexWrap="wrap" justify="flex-end">
            {visible.map(item => (
              <HStack
                key={item.id}
                spacing={1.5}
                bg="gray.50"
                borderWidth="1px"
                borderColor="brand.border"
                borderRadius="md"
                px={1.5}
                py={1}
                maxW="180px"
              >
                {item.thumb ? (
                  <Image
                    src={item.thumb}
                    alt={item.title || ''}
                    boxSize={`${THUMB_PX}px`}
                    objectFit="cover"
                    borderRadius="sm"
                    flexShrink={0}
                  />
                ) : (
                  <Box
                    boxSize={`${THUMB_PX}px`}
                    borderRadius="sm"
                    bg="gray.200"
                    flexShrink={0}
                  />
                )}
                {item.title && (
                  <Text fontSize="11px" color="brand.ink" noOfLines={2} lineHeight="1.2">
                    {item.title}
                  </Text>
                )}
              </HStack>
            ))}
            {overflow > 0 && (
              <Badge colorScheme="gray" variant="subtle" fontSize="10px" alignSelf="center">
                +{overflow} more
              </Badge>
            )}
          </HStack>
        )}
        {/* Deep-link / partial hydrate: we have product ids but no rows
            lifted — keep the count text only (no invented product API). */}
        {!hasThumbs && count > 0 && items.length === 0 && null}
      </VStack>
    </HStack>
  );
}

// Human-readable preview breakdown. When byMode is present (Phase 3+),
// surface the deterministic/director video split; image remainder is
// total − video modes (or byVariantKind when byMode is absent).
export function formatPreviewBreakdown(preview: PreviewResponse): string {
  const parts: string[] = [];
  if (preview.byMode) {
    const det = preview.byMode.deterministic ?? 0;
    const dir = preview.byMode.director ?? 0;
    if (det > 0) parts.push(`${det} standard video`);
    if (dir > 0) parts.push(`${dir} director variant${dir === 1 ? '' : 's'}`);
    const videoTotal = det + dir;
    const imageCount = Math.max(0, (preview.total ?? 0) - videoTotal);
    if (imageCount > 0) {
      parts.push(`${imageCount} image${imageCount === 1 ? '' : 's'}`);
    } else if (det === 0 && dir === 0) {
      // byMode present but zero video — fall through to variant-kind labels
      const pi = preview.byVariantKind?.product_image ?? 0;
      const ugc = preview.byVariantKind?.ugc ?? 0;
      if (pi > 0) parts.push(`${pi} product image${pi === 1 ? '' : 's'}`);
      if (ugc > 0) parts.push(`${ugc} UGC`);
    }
  } else {
    const pi = preview.byVariantKind?.product_image ?? 0;
    const ugc = preview.byVariantKind?.ugc ?? 0;
    parts.push(`${pi} product image${pi === 1 ? '' : 's'}`);
    parts.push(`${ugc} UGC`);
  }

  // `≤3 per product` used to be appended UNCONDITIONALLY, which produced the
  // self-contradiction "12 creatives (12 standard video, ≤3 per product)" for a
  // single-product batch. Measured in the live app 2026-08-27.
  //
  // The clause is not merely misplaced — on the video path it is FALSE. The
  // real cap is per-(product, KIND) in campaignAdsGenerationService:
  //   • images  → ADS_PER_PRODUCT_CAP, default 3. The clause is true here.
  //   • Director video → VEO_ADS_PER_PRODUCT_CAP, default 1.
  //   • DETERMINISTIC video → NO CAP AT ALL. That file says so outright:
  //     "No VEO_ADS_PER_PRODUCT_CAP — always exactly one ad per product that
  //     has a resolvable seed, per format call." Meta mints 4 surfaces × 3
  //     funnel stages = 12 ads per product off ONE billable master, which is
  //     correct and is exactly the 12 that made the line contradict itself.
  // So the clause is now scoped to the arm it actually describes, and names the
  // unit it caps. A video-only batch gets no cap clause, because there is no
  // per-product cap to state.
  //
  // KNOWN GAP, not fixed here: "3" is min(3, ADS_PER_PRODUCT_CAP) but the real
  // backend count is that times staticFanoutCount (campaignAdsGenerationService
  // .js:1660-1673 — "N concepts survive PER SIZE, real billable count is
  // concepts x sizes"), so a multi-format-size static run undercounts this
  // clause. Not fixed here because the only per-product number the frontend
  // has (preview.byProduct) sums images AND video together, so deriving from
  // it would resurrect the self-contradiction above for any video-containing
  // batch. Needs an images-only per-product field from the backend to fix
  // correctly — see PR discussion, not a regression from this change.
  const imageTotal = (preview.byVariantKind?.product_image ?? 0) + (preview.byVariantKind?.ugc ?? 0);
  const videoTotal = (preview.byMode?.deterministic ?? 0) + (preview.byMode?.director ?? 0);
  const imagesInBatch = preview.byMode
    ? Math.max(0, (preview.total ?? 0) - videoTotal) > 0
    : imageTotal > 0;
  if (imagesInBatch) parts.push('≤3 images per product');

  return parts.join(', ');
}

// What the run actually COSTS, separate from formatPreviewBreakdown's mode/kind
// breakdown of what it DELIVERS. A one-click express run (e.g. "Everything")
// can preview as "48 creatives" while billing for only 8 Omni submits — the
// gap is real (free 1:1 crops + free funnel re-titles), but on a one-click
// button it reads as indistinguishable from a ~6x larger bill unless this is
// called out on its own. Counts only — no dollar amounts, since per-unit
// prices aren't pinned in the UI and guessing would be worse than saying
// nothing (task instruction).
//
// Returns null when the backend hasn't shipped the `billable` block yet
// (older backend) — callers must render today's text unchanged in that case,
// not "0 billable" or any other invented number.
export function formatBillableSummary(preview: PreviewResponse): string | null {
  const billable = preview.billable;
  if (!billable) return null;
  // Scene reel: billed in scene clips + stills, with videoMasters and images
  // both 0 — the arm below would read that as "Nothing billable" and call a
  // paid reel free. Step 4 renders scene previews through
  // formatSceneReelPreview; this arm is the backstop for any other caller.
  const sceneBillable = formatSceneReelBillable(billable);
  if (sceneBillable) return sceneBillable;
  const parts: string[] = [];
  if (billable.videoMasters > 0) {
    parts.push(`${billable.videoMasters} billable video master${billable.videoMasters === 1 ? '' : 's'}`);
  }
  if (billable.images > 0) {
    parts.push(`${billable.images} billable image${billable.images === 1 ? '' : 's'}`);
  }
  if (!parts.length) {
    // Nothing billable at all — unusual (an empty preview) but not
    // impossible; say so plainly rather than rendering an empty sentence.
    return 'Nothing billable in this run';
  }
  const billableLabel = parts.join(' + ');
  return billable.freeDerived > 0
    ? `${billableLabel} + ${billable.freeDerived} free variant${billable.freeDerived === 1 ? '' : 's'}`
    : billableLabel;
}

// ── Scene reel (API contract §3) ──────────────────────────────────────────
//
// Copy rules, enforced by ui-smoke (journeys.js scans the whole page): never
// "video generation", "image generation", "master" or "size". A scene reel
// bills scene clips and stills; say exactly that.

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function usd(n: number): string {
  return `$${n.toFixed(2)}`;
}

/** "8 billable scene clips + 6–16 billable stills + 1 free 1:1 crop", or null when not a scene reel. */
export function formatSceneReelBillable(billable: PreviewResponse['billable']): string | null {
  if (!billable || typeof billable.sceneClips !== 'number' || billable.sceneClips <= 0) return null;
  const parts = [`${plural(billable.sceneClips, 'billable scene clip')}`];
  const stills = billable.stills;
  if (stills && stills.max > 0) {
    parts.push(stills.min === stills.max
      ? plural(stills.max, 'billable still')
      : `${stills.min}–${stills.max} billable stills`);
  }
  if (billable.freeDerived > 0) parts.push(`${billable.freeDerived} free 1:1 crop${billable.freeDerived === 1 ? '' : 's'}`);
  return parts.join(' + ');
}

export type SceneReelSummaryLines = {
  /** "3 videos" — what the run delivers. */
  headline:  string;
  /** "1 product · 9:16 · 16:9 · 1:1 · 10 s each (3 + 3 + 2 + 2 s)". */
  outputs:   string;
  /** What it bills; null until the dry run lands. */
  billable:  string | null;
  /** Dollar range + ceiling; null when neither preview nor catalog has one. */
  estimate:  string | null;
  /** High end of the run estimate. Null when there is no dollar estimate. */
  estimateHighUsd: number | null;
  /** Per-product asset warnings from the dry run, plus unusable products. */
  warnings:  string[];
  /** True when the numbers come from the dry run, false for the catalog fallback. */
  fromPreview: boolean;
};

/**
 * Counts, estimate and ceiling for a scene-reel run. Reads the dry run when it
 * has landed and falls back to the workflow catalog (3 videos per product, the
 * catalog's per-product estimate) while it is pending. Never back-derives a
 * billable count from `total` — only the dry run knows how many stills.
 */
export function formatSceneReelPreview(
  preview: PreviewResponse | null,
  ctx: { productCount: number; workflow: WorkflowEntry | null }
): SceneReelSummaryLines {
  const sr = preview?.sceneReel || null;
  const outputsList = sr?.outputs?.length
    ? sr.outputs
    : (ctx.workflow?.outputs.map(o => o.aspect) ?? ['9:16', '16:9', '1:1']);
  const cut = sr?.cut?.length ? sr.cut : (ctx.workflow?.cut ?? [3, 3, 2, 2]);
  const seconds = cut.reduce((a, b) => a + b, 0);
  const products = ctx.productCount;
  const videos = typeof preview?.total === 'number' && sr ? preview.total : products * outputsList.length;

  const perProduct = sr?.estUsd?.perProduct ?? ctx.workflow?.estUsdPerProduct ?? null;
  const run = sr?.estUsd?.run
    ?? (perProduct ? { low: perProduct.low * products, high: perProduct.high * products } : null);
  const ceiling = sr?.estUsd?.ceiling ?? ctx.workflow?.ceilingUsdPerProduct ?? null;
  const estimateParts: string[] = [];
  if (perProduct) estimateParts.push(`≈ ${usd(perProduct.low)}–${usd(perProduct.high)} per product (estimate)`);
  if (run && products > 1) estimateParts.push(`≈ ${usd(run.low)}–${usd(run.high)} this run`);
  if (ceiling != null) estimateParts.push(`hard ceiling ${usd(ceiling)} per product`);

  const warnings: string[] = [];
  for (const a of sr?.assets ?? []) {
    // Not "nothing is billed": Generate's source check labels unlabelled
    // images and runs person checks (small, ledgered) BEFORE it can decide a
    // product is unusable. What is never bought for it is a clip or a still.
    if (!a.usable) warnings.push('A product has no usable images for a scene reel — it will be skipped, and no scene clips or stills are billed for it.');
    for (const w of a.warnings ?? []) if (w) warnings.push(w);
  }

  return {
    headline:    plural(videos, 'video'),
    outputs:     `${plural(products, 'product')} · ${outputsList.join(' · ')} · ${seconds} s each (${cut.join(' + ')} s)`,
    billable:    preview ? formatSceneReelBillable(preview.billable) : null,
    estimate:    estimateParts.length ? estimateParts.join(' · ') : null,
    estimateHighUsd: run && typeof run.high === 'number' && Number.isFinite(run.high) ? run.high : null,
    warnings,
    fromPreview: !!sr
  };
}

function SceneReelOutput({ summary, loading }: { summary: SceneReelSummaryLines; loading: boolean }) {
  return (
    <Box>
      <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">Output</Text>
      <HStack spacing={2} mt={1} align="flex-start">
        <Text fontSize="lg" fontWeight="800" color="brand.ink">{summary.headline}</Text>
        <Text fontSize="sm" color="brand.muted">
          ({summary.outputs}{summary.fromPreview ? '' : '; dry run pending'})
        </Text>
        {loading && <Spinner size="xs" color="brand.muted" />}
      </HStack>
      {summary.billable && (
        <Badge
          mt={1.5}
          colorScheme="orange"
          variant="subtle"
          fontSize="11px"
          fontWeight="800"
          px={2}
          py={0.5}
          borderRadius="md"
          whiteSpace="normal"
        >
          {summary.billable}
        </Badge>
      )}
      {summary.estimate && (
        <Text fontSize="11px" color="brand.muted" mt={1}>{summary.estimate}</Text>
      )}
      {summary.warnings.map((w, i) => (
        <Text key={i} fontSize="11px" color="orange.600" mt={0.5}>• {w}</Text>
      ))}
    </Box>
  );
}
